import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getEventUrl } from '../../utils/urlHelpers';
import { firstImageOrPlaceholder, IMAGE_PLACEHOLDER_PATH } from '../../utils/imageFallback';

const EventsGrid = ({ events, societySlug = null, onEventClick }) => {
  const navigate = useNavigate();

  const handleEventClick = (event) => {
    if (onEventClick) {
      onEventClick(event);
    } else {
      navigate(getEventUrl(event, societySlug));
    }
  };

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
        <p className="text-gray-500">There are no events available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {events.map((event) => (
        <div 
          key={event.id} 
          className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow flex flex-col h-full cursor-pointer overflow-hidden"
          onClick={() => handleEventClick(event)}
        >
          {/* Event Image with Title Overlay */}
          <div className="aspect-w-16 aspect-h-9 relative">
            <div className="w-full h-48 bg-gray-200 flex items-center justify-center relative overflow-hidden">
              <img
                src={firstImageOrPlaceholder(event.images)}
                alt={event.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                }}
              />
              {/* Title Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end">
                <div className="p-3 w-full">
                  <h3 className="text-white font-semibold text-lg line-clamp-2 drop-shadow-lg">
                    {event.name}
                  </h3>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 flex flex-col flex-grow">
            {/* Event Info */}
            <div className="mb-4 flex-grow">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600 capitalize">{event.category || 'Event'}</p>
                {!event.is_public && (
                  <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                    Private
                  </span>
                )}
              </div>
              
              <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                {event.details || 'No details available'}
              </p>
              
              {/* Event Details */}
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {event.created_at ? new Date(event.created_at).toLocaleDateString() : 'Date TBD'}
                </div>
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {event.venue || 'Venue TBD'}
                </div>
                {event.host && (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Host: {event.host}
                  </div>
                )}
                {event.special_guest && (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    Special Guest: {event.special_guest}
                  </div>
                )}
                {event.contact_phone && (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <a 
                      href={`tel:${event.contact_phone}`}
                      className="text-blue-600 hover:text-blue-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {event.contact_phone}
                    </a>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    {event.views || 0} views
                  </div>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="mt-auto">
              <button 
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEventClick(event);
                }}
              >
                View Details
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EventsGrid;
